import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LangProvider } from "@/context/LangContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import Markets from "@/pages/Markets";
import Signals from "@/pages/Signals";
import AIAnalystPublic from "@/pages/AIAnalystPublic";
import PaperTradingPublic from "@/pages/PaperTradingPublic";
import BacktestingPublic from "@/pages/BacktestingPublic";
import Academy from "@/pages/Academy";
import AcademyArticle from "@/pages/AcademyArticle";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Legal from "@/pages/Legal";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import Onboarding from "@/pages/Onboarding";

import Dashboard from "@/pages/app/Dashboard";
import AppMarkets from "@/pages/app/AppMarkets";
import AppSignals from "@/pages/app/AppSignals";
import AppAnalyst from "@/pages/app/AppAnalyst";
import AppPaperTrading from "@/pages/app/AppPaperTrading";
import AppBacktesting from "@/pages/app/AppBacktesting";
import AppRisk from "@/pages/app/AppRisk";
import AppWatchlist from "@/pages/app/AppWatchlist";
import AppAlerts from "@/pages/app/AppAlerts";
import AppReferrals from "@/pages/app/AppReferrals";
import Account from "@/pages/app/Account";
import SignalDetail from "@/pages/app/SignalDetail";
import Admin from "@/pages/admin/Admin";
import TrustCenter from "@/pages/TrustCenter";
import KHome from "@/pages/kreonis/KHome";
import KMarkets from "@/pages/kreonis/KMarkets";
import KIntelligence from "@/pages/kreonis/KIntelligence";
import KAnalyst from "@/pages/kreonis/KAnalyst";
import KSimulation from "@/pages/kreonis/KSimulation";
import KBacktest from "@/pages/kreonis/KBacktest";
import KPricing from "@/pages/kreonis/KPricing";
import KOnboardingEmail from "@/pages/kreonis/KOnboardingEmail";
import KLegal from "@/pages/kreonis/KLegal";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

function Splash() {
  return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
}

function Protected({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return <Splash />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LangProvider>
          <CurrencyProvider>
            <BrowserRouter>
          <Routes>
            <Route path="/" element={<KHome />} />
            <Route path="/novatrade" element={<Home />} />
            <Route path="/markets" element={<Markets />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/ai-analyst" element={<AIAnalystPublic />} />
            <Route path="/paper-trading" element={<PaperTradingPublic />} />
            <Route path="/backtesting" element={<BacktestingPublic />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/academy/:slug" element={<AcademyArticle />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/trust-center" element={<TrustCenter />} />

            <Route path="/kreonis" element={<KHome />} />
            <Route path="/kreonis/markets" element={<KMarkets />} />
            <Route path="/kreonis/intelligence" element={<KIntelligence />} />
            <Route path="/kreonis/analyst" element={<KAnalyst />} />
            <Route path="/kreonis/simulation" element={<KSimulation />} />
            <Route path="/kreonis/backtest" element={<KBacktest />} />
            <Route path="/kreonis/pricing" element={<KPricing />} />
            <Route path="/kreonis/onboarding-email" element={<KOnboardingEmail />} />
            <Route path="/kreonis/legal" element={<KLegal />} />
            <Route path="/risk-disclosure" element={<Legal page="risk-disclosure" />} />
            <Route path="/terms" element={<Legal page="terms" />} />
            <Route path="/privacy" element={<Legal page="privacy" />} />
            <Route path="/cookie-policy" element={<Legal page="cookie-policy" />} />
            <Route path="/ai-disclaimer" element={<Legal page="ai-disclaimer" />} />
            <Route path="/responsible-trading" element={<Legal page="responsible-trading" />} />

            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/reset-password" element={<Reset />} />
            <Route path="/onboarding" element={<Onboarding />} />

            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/markets" element={<Protected><AppMarkets /></Protected>} />
            <Route path="/app/signals" element={<Protected><AppSignals /></Protected>} />
            <Route path="/app/signal" element={<Protected><SignalDetail /></Protected>} />
            <Route path="/app/analyst" element={<Protected><AppAnalyst /></Protected>} />
            <Route path="/app/paper-trading" element={<Protected><AppPaperTrading /></Protected>} />
            <Route path="/app/backtesting" element={<Protected><AppBacktesting /></Protected>} />
            <Route path="/app/risk" element={<Protected><AppRisk /></Protected>} />
            <Route path="/app/watchlist" element={<Protected><AppWatchlist /></Protected>} />
            <Route path="/app/alerts" element={<Protected><AppAlerts /></Protected>} />
            <Route path="/app/referrals" element={<Protected><AppReferrals /></Protected>} />
            <Route path="/app/account" element={<Protected><Account /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
            </BrowserRouter>
          </CurrencyProvider>
        </LangProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
